import { Controller } from "@hotwired/stimulus"
import { flyAircraft, preloadAircraft } from "../flyby"

// FD-132: light / dark theme toggle. One pick flips between the two themes — there
// is no "system" mode. It lives on the account menu's "Dark mode" checkbox item
// (shared/_theme_toggle). The look is driven by attributes that move together:
//   <html data-theme="light|dark">         — Cornerstone design tokens
//   <body data-theme="cru-light|cru-dark"> — daisyUI bridge themes
//   <html class="cs-light|cs-dark">       — Cornerstone Components (cs-* elements)
// plus the <meta name="color-scheme"> / <meta name="theme-color"> tags. The chosen
// theme persists in the `theme` cookie, which the server reads to render the right
// theme on first paint (no flash); this controller applies the flip live and keeps
// the cookie in sync.
const YEAR = 60 * 60 * 24 * 365

export default class extends Controller {
  connect() {
    preloadAircraft() // FD-288: warm the flyby image cache so the first switch paints instantly
  }

  // The theme currently painted. The server-rendered <html data-theme> is the source
  // of truth, and the `theme` cookie agrees with it.
  get theme() {
    return document.documentElement.getAttribute("data-theme") === "light" ? "light" : "dark"
  }

  toggle() {
    this.apply(this.theme === "light" ? "dark" : "light")
  }

  // Write the theme to the DOM + cookie, sync every toggle, and scramble a jet.
  apply(theme) {
    const previous = this.theme
    document.documentElement.setAttribute("data-theme", theme)
    document.body.setAttribute("data-theme", `cru-${theme}`)
    document.documentElement.classList.toggle("cs-dark", theme === "dark")
    document.documentElement.classList.toggle("cs-light", theme === "light")
    const scheme = document.querySelector('meta[name="color-scheme"]')
    if (scheme) scheme.setAttribute("content", theme)
    const color = document.querySelector('meta[name="theme-color"]')
    if (color) color.setAttribute("content", theme === "light" ? "#ffffff" : "#0f1112")
    this.setCookie("theme", theme)
    // There are two toggles (shared/_theme_toggle): the header icon button on desktop and
    // the account menu item on mobile. Sync both, whichever was used: the button shows the
    // current theme's glyph; the item names the theme it switches TO, icon and label.
    document.querySelectorAll("[data-theme-target='sun']").forEach((el) => (el.hidden = theme !== "light"))
    document.querySelectorAll("[data-theme-target='moon']").forEach((el) => (el.hidden = theme === "light"))
    const next = theme === "light" ? "dark" : "light"
    document.querySelectorAll("cs-dropdown-item[data-controller~='theme']").forEach((item) => {
      item.querySelector("cs-icon")?.setAttribute("name", `${next}_mode`)
      const label = item.querySelector("[data-theme-label]")
      if (label) label.textContent = item.dataset[`${next}Label`]
    })
    // FD-288: B-2 streaks in on the switch to dark, F-16 on the switch to light.
    if (theme !== previous) flyAircraft(theme, this.triggerOrigin())
  }

  // Viewport centre of the toggle — the flyby launches from here. The menu has closed by
  // the time the pick lands, so fall back to the menu's trigger (the avatar).
  triggerOrigin() {
    let r = this.element.getBoundingClientRect()
    if (!r.width) r = this.element.closest("cs-dropdown")?.querySelector("[slot=trigger]")?.getBoundingClientRect() ?? r
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 }
  }

  setCookie(name, value) {
    document.cookie = `${name}=${value}; path=/; max-age=${YEAR}; samesite=lax`
  }
}
